import { CommentBank } from "../manifest/models/comment.models";
import { PostData } from "../posts/post.models";
import { shuffle } from "../utils/randomization";
import { ClusteringMap, RandomizationStrategy } from "./generation.models";
import { SidebarStimuli } from "./sidebar.generator";
import { StimuliBank } from "./stimuli.bank";

export class StimuliShuffler {
  randomizeStimuli(bank: StimuliBank): StimuliBank {
    const strategy = bank.generationStrategy.randomization;
    if (strategy == null) return bank;

    return {
      ...bank,
      articles: this.randomizePostData(bank.articles, strategy.articles),
      advertisements: this.randomizePostData(
        bank.advertisements,
        strategy.advertisements,
      ),
      neutralInfographics: this.randomizePostData(
        bank.neutralInfographics,
        strategy.neutral_infographics,
      ),
      infographics: this.randomizePostData(
        bank.infographics,
        strategy.infographics,
      ),
      memes: this.randomizePostData(bank.memes, strategy.memes),
      quizzes: this.randomizePostData(bank.quizzes ?? [], strategy.quizzes),
      videos: this.randomizePostData(bank.videos ?? [], strategy.videos),
      filler: this.randomizePostData(bank.filler ?? [], strategy.filler),
      friendPosts: this.randomizePostData(
        bank.friendPosts ?? [],
        strategy.friend_posts,
      ),
      authors: this.randomizeSet(bank.authors, { shuffle: true }),
      avatars: this.randomizeSet(bank.avatars, { shuffle: true }),
      comments: this.randomizeCommentBank(bank.comments),
      sidebar: this.randomizeSidebar(bank.sidebar),
    };
  }

  randomizeSidebar(sidebar?: SidebarStimuli): SidebarStimuli | undefined {
    if (sidebar == null) return undefined;

    return {
      ...sidebar,
      sponsored: this.randomizeSet(sidebar.sponsored, { shuffle: true }),
      authors: this.randomizeSet(sidebar.authors, { shuffle: true }),
      avatars: this.randomizeSet(sidebar.avatars, { shuffle: true }),
      shortcuts: this.randomizeSet(sidebar.shortcuts, { shuffle: false }),
    };
  }

  randomizeCommentBank(bank: CommentBank): CommentBank {
    const shuffled: CommentBank = {};
    for (const key in bank) {
      if (!bank.hasOwnProperty(key)) continue;
      shuffled[key] = this.randomizeSet(bank[key], { shuffle: true });
    }

    return shuffled;
  }

  randomizePostData<T extends PostData>(
    set: T[],
    strategy: RandomizationStrategy = { shuffle: false },
  ): T[] {
    if (!strategy.shuffle) return set.slice();

    // If user specifies clusters for this post type,
    // then we need to do a clustered randomization strategy.
    // We collect the posts into the clusters specified in the manifest,
    // then randomize the order of the clusters along with randomizing the clusters themselves.
    if (strategy.clusters != null) {
      return this._randomizeClusters(set, strategy);
    }

    // Shuffle up the post order and then shuffle all the comment tags.
    // This randomizes the comments shown for a given post but also
    // ensures a consistent display order in multi stage experiments.
    const shuffled = shuffle(set).map((post) =>
      this.randomizeCommentTags(post),
    );

    return shuffled;
  }

  _randomizeClusters<T extends PostData>(
    set: T[],
    strategy: RandomizationStrategy = { shuffle: false },
  ): T[] {
    console.debug("using clustered randomization for set");

    if (strategy.clusters == null)
      throw new Error("_randomizeClusters called without a clustering map!");
    const final: T[] = [];
    const clusterMap: ClusteringMap = strategy.clusters!;
    const clusterNames: string[] = shuffle(Object.keys(clusterMap));
    console.debug(`chosen cluster order: ${clusterNames}`);

    for (const clusterName of clusterNames) {
      const cluster = clusterMap[clusterName];
      const order = shuffle(cluster.ids);
      for (const id of order) {
        const post = set.find((post) => post.id.includes(id));
        console.debug(`found post with id ${id} for cluster ${clusterName}`);

        if (post == null) {
          throw new Error(
            `_randomizeClusters could not find post with id matching ${id}`,
          );
        }

        final.push(post);
      }
    }

    console.debug(final);
    return final;
  }

  randomizeCommentTags<T extends PostData>(post: T): T {
    return {
      ...post,
      tags: shuffle(post.tags ?? []),
    };
  }

  randomizeSet<T>(
    set: T[],
    strategy: RandomizationStrategy = { shuffle: false },
  ): T[] {
    if (!strategy.shuffle) return set.slice();
    return shuffle(set);
  }
}
